import { Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { ConfigProvider, notification } from "antd";
import enUS from "antd/locale/en_US";
import urPK from "antd/locale/ur_PK";
import { logoutUser, setUser } from "./redux/userSlice";
import { useNetworkStatus } from "./hooks/useNetworkStatus";

import HomePage from "./pages/HomePage";
import CartPage from "./pages/CartPage";
import InvoicePage from "./pages/InvoicePage";
import CustomersPage from "./pages/CustomersPage";
import StatisticPage from "./pages/StatisticPage";
import StockManagementPage from "./pages/StockManagementPage";
import ProductPage from "./pages/ProductPage";
import CreateCashierPage from "./pages/CreateCashierPage";
import BranchesPage from "./pages/BranchesPage";
import Register from "./pages/auth/Register";
import Login from "./pages/auth/Login";
import ForgotPassword from "./pages/auth/ForgotPassword";
import ResetPassword from "./pages/auth/ResetPassword";
import DiningPage from "./pages/Dining";
import { apiRequest } from "./utils/apiClient";
import DevToolsProtection from "./components/DevToolsProtection";
import WhatsNewModal from "./components/WhatsNewModal";
import { useVersionChecker } from "./hooks/useVersionChecker";
import { SocketProvider } from "./contexts/SocketContext";

/* ================= PROTECTED ROUTE ================= */
const ProtectedRoute = ({ children }) => {
  const { t } = useTranslation("common");
  const [authStatus, setAuthStatus] = useState("checking"); // 'checking' | 'authenticated' | 'unauthenticated'
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user);

  useEffect(() => {
    const verifyAuth = async () => {
      // Check if user data exists in Redux (persisted by Redux Persist)
      const hasUserData = user?.username && user?.email;
      
      // If no user data in Redux, immediately redirect
      if (!hasUserData) {
        setAuthStatus("unauthenticated");
        return;
      }

      try {
        // Verify token with backend - sync user (including role) from response
        const data = await apiRequest("/api/auth/verify", { method: "GET" }, true);
        if (data?.user) {
          dispatch(setUser(data.user));
        }
        setAuthStatus("authenticated");
      } catch (error) {
        // Token is invalid/expired - clear user data from Redux (Redux Persist will clear localStorage)
        dispatch(logoutUser());
        localStorage.removeItem("items");
        setAuthStatus("unauthenticated");
      }
    };

    verifyAuth();
  }, [navigate, user, dispatch]);

  // Show loading state while checking authentication
  if (authStatus === "checking") {
    return (
      <div style={{ 
        display: "flex", 
        justifyContent: "center", 
        alignItems: "center", 
        height: "100vh" 
      }}>
        <div>{t("loading")}</div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (authStatus === "unauthenticated") {
    return <Navigate to="/login" replace />;
  }

  // User is authenticated: provide socket for real-time updates, then children
  return (
    <SocketProvider>
      <WhatsNewModal />
      {children}
    </SocketProvider>
  );
};

/* ================= ADMIN-ONLY ROUTE (e.g. Statistics) ================= */
const AdminOnlyRoute = ({ children }) => {
  const user = useSelector((state) => state.user);
  const role = user?.role ?? "admin";
  if (role === "cashier") {
    return <Navigate to="/" replace />;
  }
  return children;
};

/* ================= GLOBAL KEYBOARD SHORTCUTS ================= */
const GlobalKeyboardShortcuts = () => {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleGlobalKeyDown = (event) => {
      // Keyboard shortcut: press "3" to navigate to homepage with search bar focused
      if (event.key !== "3") return;

      const activeTag = document.activeElement?.tagName;
      // Don't trigger if the user is already typing in an input-like element
      if (["INPUT", "TEXTAREA", "SELECT"].includes(activeTag || "")) {
        return;
      }

      event.preventDefault();
      
      // Navigate to homepage if not already there
      if (location.pathname !== "/") {
        navigate("/");
      }
      
      // Dispatch custom event to focus search bar
      // Small delay to ensure navigation completes first
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent("focusHomepageSearch"));
      }, 100);
    };

    window.addEventListener("keydown", handleGlobalKeyDown);
    return () => {
      window.removeEventListener("keydown", handleGlobalKeyDown);
    };
  }, [navigate, location.pathname]);

  return null;
};

const OFFLINE_NOTIFICATION_KEY = "app-offline";

/* ================= APP ================= */
function App() {
  const { t, i18n: i18nInstance } = useTranslation("common");
  const cart = useSelector((state) => state.cart);
  const antdLocale = i18nInstance.language === "ur" ? urPK : enUS;
  const { isOnline } = useNetworkStatus();

  // Check for newer deployed versions and prompt the user to refresh
  useVersionChecker();

  /* Show notification when offline, clear when back online */
  useEffect(() => {
    if (!isOnline) {
      notification.warning({
        key: OFFLINE_NOTIFICATION_KEY,
        message: t("offlineTitle"),
        description: t("offlineMessage"),
        duration: 0,
        placement: "top",
      });
    } else {
      notification.destroy(OFFLINE_NOTIFICATION_KEY);
    }
  }, [isOnline, t]);

  /* Persist cart safely */
  useEffect(() => {
    try {
      localStorage.setItem("cart", JSON.stringify(cart));
    } catch (error) {
      console.error("Failed to save cart:", error);
    }
  }, [cart]);

  return (
    <>
      <ConfigProvider locale={antdLocale}>
        <DevToolsProtection />
      <GlobalKeyboardShortcuts />
      <Routes>
        {/* Protected Routes */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <HomePage />
            </ProtectedRoute>
          }
        />

      <Route
        path="/cart"
        element={
          <ProtectedRoute>
            <CartPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/invoices"
        element={
          <ProtectedRoute>
            <InvoicePage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/customers"
        element={
          <ProtectedRoute>
            <CustomersPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/statistics"
        element={
          <ProtectedRoute>
            <AdminOnlyRoute>
              <StatisticPage />
            </AdminOnlyRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/stock-management"
        element={
          <ProtectedRoute>
            <AdminOnlyRoute>
              <StockManagementPage />
            </AdminOnlyRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/dining"
        element={
          <ProtectedRoute>
            <DiningPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/products"
        element={
          <ProtectedRoute>
            <ProductPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/branches"
        element={
          <ProtectedRoute>
            <AdminOnlyRoute>
              <BranchesPage />
            </AdminOnlyRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/create-cashier"
        element={
          <ProtectedRoute>
            <AdminOnlyRoute>
              <CreateCashierPage />
            </AdminOnlyRoute>
          </ProtectedRoute>
        }
      />

      {/* Public Routes */}
      <Route path="/register" element={<Register />} />
      <Route path="/login" element={<Login />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </ConfigProvider>
    </>
  );
}

export default App;
